import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
  Eye, Edit, RefreshCw, Database, AlertTriangle, ShieldCheck,
  Zap, Hand, Mail, Users, Clock, Copy, Route, Plus, X
} from 'lucide-react';
import { emailRulesApi, emailTemplatesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Label dei trigger
const TRIGGER_LABELS = {
  appointment_created: { label: 'Appuntamento creato', color: 'bg-blue-100 text-blue-800' },
  appointment_modified: { label: 'Appuntamento modificato', color: 'bg-blue-100 text-blue-800' },
  appointment_cancelled: { label: 'Appuntamento annullato', color: 'bg-blue-100 text-blue-800' },
  appointment_reminder: { label: 'Promemoria appuntamento', color: 'bg-cyan-100 text-cyan-800' },
  lead_assigned: { label: 'Lead assegnato', color: 'bg-green-100 text-green-800' },
  contract_approved: { label: 'Contratto approvato', color: 'bg-amber-100 text-amber-800' },
  sal_updated: { label: 'Aggiornamento SAL', color: 'bg-indigo-100 text-indigo-800' },
  estimate_validation_required: { label: 'Preventivo da validare', color: 'bg-purple-100 text-purple-800' },
  manual: { label: 'Manuale', color: 'bg-gray-100 text-gray-700' },
};

// Label fallback destinatari (sovrascritte dalle options API)
const RECIPIENT_FALLBACK_LABELS = {
  client: 'Cliente',
  assigned_technician: 'Tecnico assegnato',
  assigned_work_user: 'Responsabile Lavori assegnato',
  assigned_furniture_user: 'Commerciale Arredi assegnato',
  project_manager: 'Project Manager',
  admin: 'Admin',
  administration: 'Amministrazione',
  appointment_participants: 'Partecipanti appuntamento',
  custom: 'Email personalizzate',
};

// Label condizioni note (per editor user-friendly)
const CONDITION_LABELS = {
  skip_if_no_client_email: 'Salta se il cliente non ha email',
  respect_participant_send_email_flag: 'Rispetta flag "invia email" dei partecipanti',
  skip_if_same_technician: 'Non reinviare se lo stesso tecnico',
  appointment_types: 'Tipi appuntamento inclusi',
  milestone_every_percent: 'Invia ogni X% di avanzamento SAL',
};

const EMAIL_REGEX = /^[\w.+-]+@[\w.-]+\.\w+$/;

const parseEmailList = (text) =>
  (text || '').split(',').map((e) => e.trim()).filter(Boolean);

const EmailRulesManager = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';

  // State
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ total: 0, active_count: 0 });
  const [options, setOptions] = useState({ recipient_types: [], templates: [] });
  const [seeding, setSeeding] = useState(false);

  // Editor
  const [editingRule, setEditingRule] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customEmailsText, setCustomEmailsText] = useState('');
  const [ccText, setCcText] = useState('');
  const [bccText, setBccText] = useState('');

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRule, setPreviewRule] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const recipientLabel = useCallback((type) => {
    const fromOptions = options.recipient_types.find((r) => r.value === type);
    return fromOptions?.label || RECIPIENT_FALLBACK_LABELS[type] || type;
  }, [options.recipient_types]);

  const templateName = useCallback((templateKey) => {
    if (!templateKey) return null;
    const tpl = options.templates.find((t) => t.template_key === templateKey);
    return tpl?.name || templateKey;
  }, [options.templates]);

  // Load data
  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await emailRulesApi.list();
      setRules(response.data.rules || []);
      setStats({
        total: response.data.total || 0,
        active_count: response.data.active_count || 0,
      });
    } catch (err) {
      toast.error('Errore nel caricamento delle regole');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const response = await emailRulesApi.options();
      setOptions(response.data || { recipient_types: [], templates: [] });
    } catch (err) {
      console.error('Error loading rule options:', err);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadOptions();
  }, [loadRules, loadOptions]);

  // Seed default rules
  const handleSeedDefaults = async () => {
    if (!window.confirm('Creare le regole predefinite?\n\nLe regole già esistenti non verranno duplicate.')) {
      return;
    }
    try {
      setSeeding(true);
      const response = await emailRulesApi.seedDefaults();
      const { created, skipped } = response.data;
      toast.success(`Regole default: ${created} create, ${skipped} già esistenti`);
      loadRules();
    } catch (err) {
      toast.error('Errore nella creazione delle regole default');
    } finally {
      setSeeding(false);
    }
  };

  // Toggle attivo/disattivo con rollback in caso di errore
  const handleToggleActive = async (rule) => {
    const newState = !rule.is_active;
    // Aggiornamento ottimistico
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, is_active: newState } : r)));
    setStats((prev) => ({ ...prev, active_count: prev.active_count + (newState ? 1 : -1) }));

    try {
      await emailRulesApi.update(rule.id, { is_active: newState });
      toast.success(
        newState
          ? `Regola "${rule.name}" attivata`
          : `Regola "${rule.name}" disattivata: le email di questo flusso non verranno inviate`
      );
    } catch (err) {
      // Rollback UI
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, is_active: rule.is_active } : r)));
      setStats((prev) => ({ ...prev, active_count: prev.active_count + (newState ? -1 : 1) }));
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore nel cambio stato: modifica annullata');
    }
  };

  // Editor
  const handleEdit = (rule) => {
    setEditingRule({ ...rule, conditions: { ...(rule.conditions || {}) } });
    setCustomEmailsText((rule.custom_emails || []).join(', '));
    setCcText((rule.cc || []).join(', '));
    setBccText((rule.bcc || []).join(', '));
    setEditorOpen(true);
  };

  const validateEmails = (list, fieldName) => {
    for (const email of list) {
      if (!EMAIL_REGEX.test(email)) {
        toast.error(`Email non valida in ${fieldName}: ${email}`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!editingRule) return;

    const customEmails = parseEmailList(customEmailsText);
    const ccList = parseEmailList(ccText);
    const bccList = parseEmailList(bccText);

    if (!validateEmails(customEmails, 'Email personalizzate')) return;
    if (!validateEmails(ccList, 'CC')) return;
    if (!validateEmails(bccList, 'BCC')) return;

    if (customEmails.length > 0 && !editingRule.recipients.includes('custom')) {
      toast.error('Hai inserito email personalizzate: seleziona anche il destinatario "Email personalizzate"');
      return;
    }

    const isReminderRule = editingRule.trigger_event === 'appointment_reminder';
    if (isReminderRule) {
      const offset = Number(editingRule.reminder_offset_hours);
      if (!offset || offset < 1 || offset > 168) {
        toast.error('L\'offset promemoria deve essere tra 1 e 168 ore');
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        name: editingRule.name,
        description: editingRule.description,
        recipients: editingRule.recipients,
        custom_emails: customEmails,
        cc: ccList,
        bcc: bccList,
        conditions: editingRule.conditions || {},
        is_active: editingRule.is_active,
      };
      // Template modificabile solo se la regola usa un template (non contenuto dedicato)
      if (editingRule.template_key) {
        payload.template_key = editingRule.template_key;
      }
      if (isReminderRule) {
        payload.reminder_offset_hours = Number(editingRule.reminder_offset_hours);
      }

      await emailRulesApi.update(editingRule.id, payload);
      toast.success('Regola salvata con successo');
      setEditorOpen(false);
      setEditingRule(null);
      loadRules();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map((d) => d.msg).join(' | '));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Errore nel salvataggio della regola');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleRecipient = (type) => {
    setEditingRule((prev) => {
      const recipients = prev.recipients.includes(type)
        ? prev.recipients.filter((r) => r !== type)
        : [...prev.recipients, type];
      return { ...prev, recipients };
    });
  };

  const updateCondition = (key, value) => {
    setEditingRule((prev) => ({
      ...prev,
      conditions: { ...(prev.conditions || {}), [key]: value },
    }));
  };

  // Anteprima template della regola
  const handlePreview = async (rule) => {
    if (!rule.template_key) {
      toast.info('Questa regola usa un contenuto email dedicato, non un template configurabile');
      return;
    }
    try {
      setPreviewRule(rule);
      setPreviewData(null);
      setPreviewLoading(true);
      setPreviewOpen(true);
      const response = await emailTemplatesApi.previewByKey(rule.template_key);
      setPreviewData(response.data);
    } catch (err) {
      toast.error('Errore nella generazione anteprima');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Render condizione in lista (compatta)
  const renderConditionSummary = (conditions) => {
    const keys = Object.keys(conditions || {});
    if (keys.length === 0) return null;
    return keys.map((key) => {
      const value = conditions[key];
      let text = CONDITION_LABELS[key] || key;
      if (Array.isArray(value)) text += `: ${value.join(', ')}`;
      else if (typeof value === 'number') text += `: ${value}`;
      else if (value === false) text = `${text}: NO`;
      return (
        <Badge key={key} variant="outline" className="text-xs text-gray-500 font-normal">
          {text}
        </Badge>
      );
    });
  };

  if (loading) {
    return (
      <Card className="card-abitalo">
        <CardContent className="p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#FF7819]" />
          <p className="mt-4 text-gray-500">Caricamento regole...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-rules-manager">
      {/* Header */}
      <Card className="card-abitalo">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Route className="w-5 h-5 text-[#FF7819]" />
                Regole Invio Email
              </CardTitle>
              <CardDescription>
                Governa chi riceve cosa e quando: trigger, template, destinatari, CC/BCC e condizioni
              </CardDescription>
            </div>
            {canEdit && (
              <Button
                onClick={handleSeedDefaults}
                disabled={seeding}
                variant="outline"
                className="border-[#FF7819] text-[#FF7819] hover:bg-[#FF7819]/10"
                data-testid="email-rules-seed-btn"
              >
                <Database className="w-4 h-4 mr-2" />
                {seeding ? 'Creazione...' : 'Crea Regole Default'}
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Banner sicurezza fallback */}
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-3"
        data-testid="email-rules-fallback-banner"
      >
        <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-800">
          <strong>Comportamento sicuro garantito:</strong> se una regola viene eliminata o presenta problemi,
          il CRM usa automaticamente il comportamento standard. Disattivando una regola, le email di quel
          flusso <strong>non verranno inviate</strong> finché non la riattivi.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-abitalo">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#FF7819]" data-testid="email-rules-stat-total">{stats.total}</p>
            <p className="text-sm text-gray-500">Regole Totali</p>
          </CardContent>
        </Card>
        <Card className="card-abitalo">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-emerald-600" data-testid="email-rules-stat-active">{stats.active_count}</p>
            <p className="text-sm text-gray-500">Attive</p>
          </CardContent>
        </Card>
        <Card className="card-abitalo">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-500" data-testid="email-rules-stat-inactive">{stats.total - stats.active_count}</p>
            <p className="text-sm text-gray-500">Disattivate</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista regole */}
      <Card className="card-abitalo">
        <CardContent className="p-0">
          {rules.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Nessuna regola configurata. Il CRM usa il comportamento standard.</p>
              {canEdit && (
                <Button
                  onClick={handleSeedDefaults}
                  className="mt-4 bg-[#FF7819] hover:bg-[#e66a0f]"
                  data-testid="email-rules-seed-empty-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Regole Default
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {rules.map((rule) => {
                const trigger = TRIGGER_LABELS[rule.trigger_event] || { label: rule.trigger_event, color: 'bg-gray-100 text-gray-700' };
                const tplName = templateName(rule.template_key);
                const tplMeta = options.templates.find((t) => t.template_key === rule.template_key);
                return (
                  <div
                    key={rule.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${!rule.is_active ? 'opacity-60' : ''}`}
                    data-testid={`email-rule-row-${rule.rule_key}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Nome + badge */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium text-[#3C3C3C]">{rule.name}</h3>
                          <Badge className={trigger.color}>{trigger.label}</Badge>
                          <Badge variant="outline" className={rule.mode === 'automatic' ? 'text-emerald-700 border-emerald-300' : 'text-gray-600'}>
                            {rule.mode === 'automatic' ? (
                              <><Zap className="w-3 h-3 mr-1" /> Automatico</>
                            ) : (
                              <><Hand className="w-3 h-3 mr-1" /> Manuale</>
                            )}
                          </Badge>
                          {tplMeta?.category && (
                            <Badge className={tplMeta.category === 'customer' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                              {tplMeta.category === 'customer' ? 'Cliente' : 'Interno'}
                            </Badge>
                          )}
                          {rule.is_system && (
                            <Badge variant="outline" className="text-xs">Sistema</Badge>
                          )}
                          {!rule.is_active && (
                            <Badge variant="secondary" className="bg-red-100 text-red-700">Disattivata</Badge>
                          )}
                        </div>

                        {/* Descrizione */}
                        {rule.description && (
                          <p className="text-sm text-gray-500 mt-1">{rule.description}</p>
                        )}

                        {/* Template collegato */}
                        <div className="flex items-center gap-2 mt-2 text-sm">
                          <Mail className="w-3.5 h-3.5 text-gray-400" />
                          {rule.template_key ? (
                            <span className="text-gray-600">
                              Template: <span className="font-medium">{tplName}</span>
                            </span>
                          ) : (
                            <span className="text-gray-500 italic">Contenuto email dedicato</span>
                          )}
                        </div>

                        {/* Destinatari */}
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          {(rule.recipients || []).map((r) => (
                            <Badge key={r} variant="secondary" className="text-xs font-normal">
                              {recipientLabel(r)}
                            </Badge>
                          ))}
                          {(rule.custom_emails || []).map((e) => (
                            <Badge key={e} variant="secondary" className="text-xs font-mono font-normal bg-amber-50 text-amber-800">
                              {e}
                            </Badge>
                          ))}
                          {(rule.recipients || []).length === 0 && (rule.custom_emails || []).length === 0 && (
                            <span className="text-xs text-gray-400 italic">Nessun destinatario</span>
                          )}
                        </div>

                        {/* CC/BCC + offset + condizioni */}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {(rule.cc || []).length > 0 && (
                            <Badge variant="outline" className="text-xs font-normal text-blue-700 border-blue-200">
                              <Copy className="w-3 h-3 mr-1" /> CC: {rule.cc.join(', ')}
                            </Badge>
                          )}
                          {(rule.bcc || []).length > 0 && (
                            <Badge variant="outline" className="text-xs font-normal text-gray-600">
                              BCC: {rule.bcc.join(', ')}
                            </Badge>
                          )}
                          {rule.reminder_offset_hours != null && (
                            <Badge variant="outline" className="text-xs font-normal text-cyan-700 border-cyan-200">
                              <Clock className="w-3 h-3 mr-1" /> {rule.reminder_offset_hours}h prima
                            </Badge>
                          )}
                          {renderConditionSummary(rule.conditions)}
                        </div>
                      </div>

                      {/* Azioni */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreview(rule)}
                          disabled={!rule.template_key}
                          title={rule.template_key ? 'Anteprima template' : 'Contenuto dedicato: anteprima non disponibile'}
                          data-testid={`email-rule-preview-btn-${rule.rule_key}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(rule)}
                              title="Modifica regola"
                              data-testid={`email-rule-edit-btn-${rule.rule_key}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Switch
                              checked={rule.is_active}
                              onCheckedChange={() => handleToggleActive(rule)}
                              title={rule.is_active ? 'Disattiva regola' : 'Attiva regola'}
                              data-testid={`email-rule-toggle-${rule.rule_key}`}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="email-rule-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#FF7819]" />
              Modifica Regola: {editingRule?.name}
            </DialogTitle>
            <DialogDescription>
              Trigger: <Badge className={TRIGGER_LABELS[editingRule?.trigger_event]?.color || 'bg-gray-100'}>
                {TRIGGER_LABELS[editingRule?.trigger_event]?.label || editingRule?.trigger_event}
              </Badge>
            </DialogDescription>
          </DialogHeader>

          {editingRule && (
            <div className="flex-1 overflow-auto space-y-5 p-1">
              {/* Nome */}
              <div className="space-y-2">
                <Label>Nome regola</Label>
                <Input
                  value={editingRule.name || ''}
                  onChange={(e) => setEditingRule((prev) => ({ ...prev, name: e.target.value }))}
                  data-testid="email-rule-editor-name"
                />
              </div>

              {/* Template */}
              <div className="space-y-2">
                <Label>Template email</Label>
                {editingRule.template_key !== null && editingRule.template_key !== undefined && editingRule.template_key !== '' ? (
                  <Select
                    value={editingRule.template_key}
                    onValueChange={(value) => setEditingRule((prev) => ({ ...prev, template_key: value }))}
                  >
                    <SelectTrigger data-testid="email-rule-editor-template-select">
                      <SelectValue placeholder="Seleziona template" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.templates.map((t) => (
                        <SelectItem key={t.template_key} value={t.template_key}>
                          {t.name} {t.is_active === false ? '(disattivo)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-gray-500 italic bg-gray-50 rounded-lg p-3">
                    Questa regola usa un contenuto email dedicato generato dal sistema (non modificabile da template).
                  </p>
                )}
              </div>

              {/* Destinatari dinamici */}
              <div className="space-y-2">
                <Label>Destinatari</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(options.recipient_types.length > 0
                    ? options.recipient_types
                    : Object.entries(RECIPIENT_FALLBACK_LABELS).map(([value, label]) => ({ value, label }))
                  ).map((rt) => (
                    <label
                      key={rt.value}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        editingRule.recipients.includes(rt.value)
                          ? 'border-[#FF7819] bg-[#FF7819]/5'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      data-testid={`email-rule-recipient-${rt.value}`}
                    >
                      <input
                        type="checkbox"
                        checked={editingRule.recipients.includes(rt.value)}
                        onChange={() => toggleRecipient(rt.value)}
                        className="accent-[#FF7819]"
                      />
                      <span className="text-sm">{rt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Email custom */}
              <div className="space-y-2">
                <Label>Email personalizzate (separate da virgola)</Label>
                <Input
                  value={customEmailsText}
                  onChange={(e) => setCustomEmailsText(e.target.value)}
                  placeholder="es. fatturazione@azienda.it, controllo@azienda.it"
                  data-testid="email-rule-editor-custom-emails"
                />
                <p className="text-xs text-gray-400">
                  Richiede il destinatario &quot;Email personalizzate&quot; selezionato sopra
                </p>
              </div>

              {/* CC / BCC */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CC (separate da virgola)</Label>
                  <Input
                    value={ccText}
                    onChange={(e) => setCcText(e.target.value)}
                    placeholder="es. cc@azienda.it"
                    data-testid="email-rule-editor-cc"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BCC (separate da virgola)</Label>
                  <Input
                    value={bccText}
                    onChange={(e) => setBccText(e.target.value)}
                    placeholder="es. archivio@azienda.it"
                    data-testid="email-rule-editor-bcc"
                  />
                </div>
              </div>

              {/* Offset promemoria (solo regola promemoria) */}
              {editingRule.trigger_event === 'appointment_reminder' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan-600" />
                    Ore prima dell&apos;appuntamento (1-168)
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={168}
                    value={editingRule.reminder_offset_hours ?? 24}
                    onChange={(e) => setEditingRule((prev) => ({ ...prev, reminder_offset_hours: e.target.value }))}
                    className="w-40"
                    data-testid="email-rule-editor-offset"
                  />
                  <p className="text-xs text-gray-400">
                    Il promemoria viene inviato automaticamente questo numero di ore prima dell&apos;appuntamento
                  </p>
                </div>
              )}

              {/* Condizioni */}
              {Object.keys(editingRule.conditions || {}).length > 0 && (
                <div className="space-y-2">
                  <Label>Condizioni di invio</Label>
                  <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                    {Object.entries(editingRule.conditions).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-700">{CONDITION_LABELS[key] || key}</span>
                        {typeof value === 'boolean' ? (
                          <Switch
                            checked={value}
                            onCheckedChange={(checked) => updateCondition(key, checked)}
                            data-testid={`email-rule-condition-${key}`}
                          />
                        ) : Array.isArray(value) ? (
                          <Input
                            value={value.join(', ')}
                            onChange={(e) => updateCondition(key, e.target.value.split(',').map((v) => v.trim()).filter(Boolean))}
                            className="max-w-[280px] text-sm"
                            data-testid={`email-rule-condition-${key}`}
                          />
                        ) : typeof value === 'number' ? (
                          <Input
                            type="number"
                            value={value}
                            onChange={(e) => updateCondition(key, Number(e.target.value))}
                            className="w-24 text-sm"
                            data-testid={`email-rule-condition-${key}`}
                          />
                        ) : (
                          <span className="text-sm text-gray-500">{String(value)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stato */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium">Regola attiva</p>
                  <p className="text-xs text-gray-500">
                    Se disattivata, le email di questo flusso non verranno inviate
                  </p>
                </div>
                <Switch
                  checked={editingRule.is_active}
                  onCheckedChange={(checked) => setEditingRule((prev) => ({ ...prev, is_active: checked }))}
                  data-testid="email-rule-editor-active-switch"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="email-rule-editor-cancel-btn">
              Annulla
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FF7819] hover:bg-[#e66a0f]"
              data-testid="email-rule-editor-save-btn"
            >
              {saving ? 'Salvataggio...' : 'Salva Regola'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="email-rule-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#FF7819]" />
              Anteprima: {previewRule?.name}
            </DialogTitle>
            <DialogDescription>
              Anteprima con dati di esempio. Nessuna email viene inviata.
            </DialogDescription>
            {previewData && (
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={previewData.source === 'db_template' ? 'default' : 'secondary'}>
                  {previewData.source === 'db_template' ? (
                    <><Database className="w-3 h-3 mr-1" /> Database</>
                  ) : (
                    <><AlertTriangle className="w-3 h-3 mr-1" /> Fallback Hardcoded</>
                  )}
                </Badge>
              </div>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {previewLoading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#FF7819]" />
                <p className="mt-4 text-gray-500">Generazione anteprima...</p>
              </div>
            ) : previewData ? (
              <div className="space-y-4">
                {/* Destinatari della regola */}
                {previewRule && (
                  <div className="bg-gray-50 p-3 rounded-lg" data-testid="email-rule-preview-recipients">
                    <p className="text-xs text-gray-500 uppercase mb-2">Chi riceverà questa email</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(previewRule.recipients || []).map((r) => (
                        <Badge key={r} variant="secondary" className="text-xs font-normal">
                          {recipientLabel(r)}
                        </Badge>
                      ))}
                      {(previewRule.custom_emails || []).map((e) => (
                        <Badge key={e} variant="secondary" className="text-xs font-mono font-normal bg-amber-50 text-amber-800">
                          {e}
                        </Badge>
                      ))}
                      {(previewRule.cc || []).length > 0 && (
                        <Badge variant="outline" className="text-xs font-normal text-blue-700 border-blue-200">
                          CC: {previewRule.cc.join(', ')}
                        </Badge>
                      )}
                      {(previewRule.bcc || []).length > 0 && (
                        <Badge variant="outline" className="text-xs font-normal text-gray-600">
                          BCC: {previewRule.bcc.join(', ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Oggetto</p>
                  <p className="font-medium" data-testid="email-rule-preview-subject">{previewData.subject}</p>
                </div>

                {/* HTML Preview */}
                <Tabs defaultValue="rendered">
                  <TabsList>
                    <TabsTrigger value="rendered">Anteprima</TabsTrigger>
                    <TabsTrigger value="text">Plain Text</TabsTrigger>
                  </TabsList>

                  <TabsContent value="rendered" className="mt-2">
                    <div
                      className="border rounded-lg p-4 bg-white max-h-[400px] overflow-auto"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.html_body) }}
                    />
                  </TabsContent>

                  <TabsContent value="text" className="mt-2">
                    <pre className="border rounded-lg p-4 bg-gray-50 text-sm font-mono overflow-auto max-h-[400px] whitespace-pre-wrap">
                      {previewData.text_body}
                    </pre>
                  </TabsContent>
                </Tabs>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="email-rule-preview-close-btn">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailRulesManager;
